import React, { useState } from "react";
import { useDepartments } from "../../context/DepartmentContext";
import { useUsers } from "../../context/UserContext";
import { useMachines } from "../../context/MachineContext";
import Layout from "../../components/layout/Layout";
import DepartmentModal from "../../components/departments/DepartmentModal";
import { useColumnSearchProps } from "../../components/departments/getColumnSearchProps";
import {
  ApartmentOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ToolOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Table, Popconfirm } from "antd";

const DepartmentsPage = () => {
  const {
    departments,
    loading,
    addDepartment,
    deleteDepartment,
    updateDepartment,
  } = useDepartments();
  const { users } = useUsers();
  const { machines } = useMachines();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); // Güncelleme modu için
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null); // Seçilen departman ID'si
  const [errors, setErrors] = useState({});
  const getColumnSearchProps = useColumnSearchProps();
  const [newDepartment, setNewDepartment] = useState({
    name: "",
    code: "",
    description: "",
    manager: "",
    location: "",
    employeeCount: 0,
    status: "Active",
    machines: [],
  });

  const handleEditDepartment = (department) => {
    setNewDepartment(department); // Mevcut makine bilgilerini yükle
    setIsEditMode(true); // Güncelleme modunu aktif et
    setSelectedDepartmentId(department._id); // Seçilen makinenin ID'sini sakla
    setIsModalOpen(true); // Modalı aç
  };

  const handleUpdateDepartment = () => {
    if (!validateDepartment()) return; // Validasyonu kontrol et

    updateDepartment(selectedDepartmentId, newDepartment);
    handleCloseModal();
  };
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const validateDepartment = () => {
    const requiredFields = [
      "name",
      "code",
      "status",
      "location",
      "employeeCount",
      "manager",
    ];
    const newErrors = {};

    requiredFields.forEach((field) => {
      if (!newDepartment[field]) {
        newErrors[field] = `${field.replace(/([A-Z])/g, " $1")} is required.`; // Dinamik hata mesajı
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Hata yoksa true döner
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false); // Güncelleme modunu kapat
    setSelectedDepartmentId(null); // Seçilen makine ID'sini temizle
    setNewDepartment({
      name: "",
      code: "",
      description: "",
      manager: "",
      location: "",
      employeeCount: 0,
      status: "Active",
      machines: [],
    });
  };

  const handleSave = () => {
    if (!validateDepartment()) return; // Eğer hata varsa işlemi durdur
    addDepartment(newDepartment);
    handleCloseModal();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return { color: "green", text: "Aktif" };
      case "Inactive":
        return { color: "red", text: "Pasif" };
      default:
        return { color: "gray", text: "Bilinmiyor" };
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("tr-TR");
  };

  const columns = [
    {
      dataIndex: "icon",
      key: "icon",
      width: "1%",
      render: () => <ApartmentOutlined style={{ color: "#1890ff" }} />
    },
    {
      title: "D.Kodu",
      dataIndex: "code",
      key: "code",
      width: "10%",
      ...getColumnSearchProps("code"),
      sorter: (a, b) => a.code.length - b.code.length,
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "D.Adı",
      dataIndex: "name",
      key: "name",
      width: "20%",
      ...getColumnSearchProps("name"),
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "D.Durumu",
      dataIndex: "status",
      key: "status",
      width: "10%",
      filters: [
        { text: "Aktif", value: "Active" },
        { text: "Pasif", value: "Inactive" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => {
        const { color, text } = getStatusColor(status);
        return <span style={{ color }}>{text}</span>;
      },
    },
    {
      title: "Lokasyon",
      dataIndex: "location",
      key: "location",
      width: "15%",
      ...getColumnSearchProps("location"),
      sorter: (a, b) => a.location.localeCompare(b.location),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Çalışanlar",
      dataIndex: "employeeCount",
      key: "employeeCount",
      width: "5%",
      sorter: (a, b) => a.employeeCount - b.employeeCount,
      sortDirections: ["descend", "ascend"],
      render: (employeeCount) => (<span>
        <UserOutlined style={{ marginRight: 8, color:"#1890ff"}} />
        {employeeCount || 0}
      </span>)
    },
    {
      title: "Makineler",
      dataIndex: "machines",
      key: "machines",
      width: "5%",
      sorter: (a, b) => a.machines.length - b.machines.length,
      sortDirections: ["descend", "ascend"],
      render: (machines) => (
        <span>
          <ToolOutlined style={{ marginRight: 8, color:"#1890ff" }} />
          {machines?.length || 0}
        </span>
      ),
    },
    {
      title: "Departman OEE",
      dataIndex: "employeeCount",
      key: "employeeCount",
      width: "13%",
      sorter: (a, b) => a.employeeCount - b.employeeCount,
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Oluşturulma Tarihi",
      dataIndex: "createdAt",
      key: "createdAt",
      width: "13%",
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      sortDirections: ["descend", "ascend"],
      render: (createdAt) => formatDate(createdAt),
    },
    {
      title: "",
      key: "actions",
      width: "10%",
      render: (department) => (
        <span>
          {/* Düzenleme Butonu */}
          <EditOutlined
            style={{ marginRight: 10 }}
            onClick={() => handleEditDepartment(department)}
          />

          {/* Silme Butonu (Popconfirm ile) */}
          <Popconfirm
            title="Bu departmanı silmek istediğinize emin misiniz?"
            onConfirm={() => deleteDepartment(department._id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <DeleteOutlined style={{ color: "red" }} />
          </Popconfirm>
        </span>
      ),
    },
  ];

  if (loading) {
    return <div>Yükleniyor...</div>;
  }

  return (
    <Layout>
      <div className="mt-16 ml-64">
        {/* Üst Alan */}
        <DepartmentModal
          isModalOpen={isModalOpen}
          handleCloseModal={handleCloseModal}
          newDepartment={newDepartment}
          setNewDepartment={setNewDepartment}
          errors={errors}
          handleSave={isEditMode ? handleUpdateDepartment : handleSave} // Güncelleme veya ekleme işlemi
          machines={machines}
          users={users}
          isEditMode={isEditMode} // Modalın güncelleme modunda olduğunu belirt
        />
        <div className="flex justify-between items-center p-4 bg-white shadow-md rounded-lg mb-4">
          <h2 className="text-xl font-semibold">Departmanlar</h2>
          <div className="flex gap-4 items-center">
            <button
              className="bg-gray-800 text-white px-4 py-2 rounded hover:bg-gray-600 flex items-center gap-2"
              onClick={handleOpenModal}
            >
              <PlusOutlined /> Departman Ekle
            </button>
          </div>
        </div>
        <Table columns={columns} dataSource={departments} />
      </div>
    </Layout>
  );
};

export default DepartmentsPage;
